// Checkbox (DESIGN §3.6.9): 18 × 18, Radius 5, aus Rand 1,5 px `fg-tertiary`, an `accent-strong`
// mit Haken `on-accent-strong`, teilweise = Strich. Trefferfläche 40 (unsichtbar erweitert).
import { useId, type ReactNode, type Ref } from 'react';
import { CheckIcon, MinusIcon } from '@phosphor-icons/react';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import { useFieldProps } from './_lib/fieldContext';
import { cn } from '../basis/kern';

export type CheckedState = boolean | 'indeterminate';

export interface CheckboxProps {
  ref?: Ref<HTMLButtonElement>;
  checked?: CheckedState;
  defaultChecked?: CheckedState;
  onCheckedChange?: (checked: CheckedState) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  id?: string;
  'aria-label'?: string;
  className?: string;
}

export function Checkbox({
  ref,
  label,
  description,
  className,
  id,
  disabled,
  ...rest
}: CheckboxProps) {
  const autoId = useId();
  const field = useFieldProps({ id });
  const boxId = field.id ?? `checkbox${autoId}`;
  const descriptionId = `${boxId}-desc`;
  const box = (
    <CheckboxPrimitive.Root
      {...rest}
      ref={ref}
      id={boxId}
      disabled={disabled}
      aria-invalid={field['aria-invalid']}
      aria-describedby={
        cn(field['aria-describedby'], description ? descriptionId : undefined) || undefined
      }
      className={cn(
        'peer relative inline-flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] border-fg-tertiary',
        'transition-colors duration-(--bar-dur-fast) ease-standard',
        'data-[state=checked]:border-accent-strong data-[state=checked]:bg-accent-strong data-[state=checked]:text-on-accent-strong',
        'data-[state=indeterminate]:border-accent-strong data-[state=indeterminate]:bg-accent-strong data-[state=indeterminate]:text-on-accent-strong',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'before:absolute before:-inset-[11px]',
      )}
    >
      <CheckboxPrimitive.Indicator className="inline-flex">
        {rest.checked === 'indeterminate' ? (
          <MinusIcon size={12} weight="bold" aria-hidden="true" />
        ) : (
          <CheckIcon size={12} weight="bold" aria-hidden="true" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return <span className={cn('inline-flex', className)}>{box}</span>;
  return (
    <div className={cn('flex items-start gap-3', className)}>
      <span className="pt-0.5">{box}</span>
      <span className="flex min-w-0 flex-col">
        <label
          htmlFor={boxId}
          className={cn('text-body', disabled ? 'text-fg-disabled' : 'text-fg')}
        >
          {label}
        </label>
        {description ? (
          <span id={descriptionId} className="text-label text-fg-secondary">
            {description}
          </span>
        ) : null}
      </span>
    </div>
  );
}
